import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { GridProps } from './types';
import {
  Container,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  SelectedCount,
  List,
} from './styles';
import { Photo } from '../Photo';
import { MainButton } from '../../inputs/buttons/MainButton';
import { resolveImageUrl } from '../../../utils/cloudinary';
import { openModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';

export const GridOfPhotos = ({
  imagesToShow,
  onDeletePhoto,
}: GridProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  const handleToggleSelection = useCallback((id: string) => {
    setSelectedPhotos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedPhotos.size === imagesToShow.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(imagesToShow.map((img) => img.id)));
    }
  }, [imagesToShow, selectedPhotos.size]);

  const handleDownloadSelected = useCallback(() => {
    const selectedImages = imagesToShow.filter((img) =>
      selectedPhotos.has(img.id)
    );
    if (selectedImages.length === 0) return;

    const roidOf = (img: (typeof selectedImages)[number]) =>
      Array.isArray(img.roid) ? img.roid.join('.') : img.roid;

    // Reutiliza el modal de formato del listado (modo directo): el usuario elige
    // calidad (Alta / Optimizada) y se descarga como ZIP. Igual que en otros
    // puntos de descarga de multimedia.
    dispatch(
      openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, {
        directImages: selectedImages.map((img) => ({
          image: img,
          orderName: roidOf(img),
        })),
      })
    );
  }, [dispatch, imagesToShow, selectedPhotos]);

  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
    if (selectionMode) {
      setSelectedPhotos(new Set());
    }
  }, [selectionMode]);

  return (
    <Container>
      <Toolbar>
        <ToolbarLeft>
          <MainButton
            text={
              selectionMode
                ? t('buttons:cancelSelection')
                : t('buttons:selectPhotos')
            }
            variant={selectionMode ? 'secondary' : 'primary'}
            onClick={handleToggleSelectionMode}
          />
          {selectionMode && (
            <>
              <MainButton
                text={
                  selectedPhotos.size === imagesToShow.length
                    ? t('buttons:deselectAll')
                    : t('buttons:selectAll')
                }
                variant="secondary"
                onClick={handleSelectAll}
              />
              {selectedPhotos.size > 0 && (
                <SelectedCount>
                  {selectedPhotos.size === 1
                    ? t('multimedia:selectedPhoto', {
                        count: selectedPhotos.size,
                      })
                    : t('multimedia:selectedPhotos', {
                        count: selectedPhotos.size,
                      })}
                </SelectedCount>
              )}
            </>
          )}
        </ToolbarLeft>
        <ToolbarRight>
          {selectionMode && selectedPhotos.size > 0 && (
            <MainButton
              text={t('buttons:downloadSelected')}
              variant="primary"
              onClick={handleDownloadSelected}
            />
          )}
        </ToolbarRight>
      </Toolbar>
      <List>
        {imagesToShow &&
          imagesToShow.map((image) => (
            <Photo
              key={image.id}
              id={image.id}
              image={image}
              imageUrl={resolveImageUrl(image, 'full')}
              title={image.name}
              date={image.creatingDate}
              roid={Array.isArray(image.roid) ? image.roid.join('.') : image.roid}
              isSelected={selectedPhotos.has(image.id)}
              onSelect={handleToggleSelection}
              selectionMode={selectionMode}
              onDelete={onDeletePhoto}
            />
          ))}
      </List>
    </Container>
  );
};
